const platformValues = {
  ANDROID: "android",
  WEBOS: "webos",
  LINUX: "linux",
  WINDOWS: "win32",
} as const;

type Platform = (typeof platformValues)[keyof typeof platformValues];

const platforms = {
  ...platformValues,
  ALL: Object.values(platformValues) as Platform[],
  DEFAULT: [
    platformValues.ANDROID,
    platformValues.LINUX,
    platformValues.WINDOWS,
  ] as Platform[],
  DESKTOP: [platformValues.LINUX, platformValues.WINDOWS] as Platform[],
};

interface FlagValidity {
  startsIn: string;
  endsIn: string | null;
}

interface FlagOption {
  label: string;
  value: string;
}

interface FlagDefinition {
  default?: string | number | null;
  endAdornment?: string;
  explanation: string;
  explanationLink?: {
    href: string;
    label: string;
  };
  flagType: string;
  key: string;
  label: string;
  options?: FlagOption[];
  platforms?: Platform[];
  type:
    | StringConstructor
    | NumberConstructor
    | BooleanConstructor
    | ArrayConstructor;
  validity?: FlagValidity;
}

type FlagName = keyof typeof flags;

type FlagWithDefaults = FlagDefinition & {
  platforms: Platform[];
};

const withDefaultPlatforms = (flag: FlagDefinition): FlagWithDefaults => ({
  platforms: platforms.DESKTOP,
  ...flag,
});

const flags = {
  CHECK_PLAYER_SECONDS: {
    default: "240",
    endAdornment: "Seconds",
    explanation: `The watchdog will check player health every X seconds.`,
    flagType: "watchdog",
    key: "--check-player-seconds",
    label: "Check Player Seconds",
    type: String,
  },
  FOCUS_WINDOW_SECONDS: {
    default: "5",
    endAdornment: "Seconds",
    explanation: `The watchdog will try to focus the player window
      every X seconds.`,
    flagType: "watchdog",
    key: "--focus-window-seconds",
    label: "Focus Window Seconds",
    type: Number,
  },
  HIDE_CURSOR_SECONDS: {
    default: "120",
    endAdornment: "Seconds",
    explanation: `The watchdog will try to hide the cursor every X seconds.`,
    flagType: "watchdog",
    key: "--hide-cursor-seconds",
    label: "Hide Cursor Seconds",
    type: Number,
  },
  MAX_MEMORY_PER_APP: {
    default: "419430400",
    endAdornment: "Bytes",
    explanation: `Sets the memory limit for each app process in bytes.
      Default is 400MBs.`,
    flagType: "other",
    key: "--max-memory-per-app",
    label: "Max Memory Per App",
    type: Number,
  },
  LOCAL_SERVER_PORT: {
    explanation: `Overrides local server port in version 2.57.0 and later.
      Default is 8000.`,
    flagType: "other",
    key: "--local-server-port",
    label: "Local Server Port",
    platforms: [platforms.LINUX, platforms.WINDOWS],
    type: Number,
    validity: {
      endsIn: null,
      startsIn: "2.57.0",
    },
  },
  WINDOW_WIDTH: {
    endAdornment: "Pixels",
    explanation: `Sets player window's width.
      Player runs in fullscreen by default.`,
    flagType: "window",
    key: "--window-width",
    label: "Window Width",
    platforms: [platforms.LINUX, platforms.WINDOWS],
    type: Number,
  },
  WINDOW_HEIGHT: {
    endAdornment: "Pixels",
    explanation: `Sets player window's height.
      Player runs in fullscreen by default.`,
    flagType: "window",
    key: "--window-height",
    label: "Window Height",
    platforms: [platforms.LINUX, platforms.WINDOWS],
    type: Number,
  },
  WINDOW_X: {
    endAdornment: "Pixels",
    explanation: `Sets player window's x coordinate position.
      Player runs in fullscreen by default.`,
    flagType: "window",
    key: "--window-x",
    label: "Window X",
    type: Number,
  },
  WINDOW_Y: {
    endAdornment: "Pixels",
    explanation: `Sets player window's y coordinate position.
      Player runs in fullscreen by default.`,
    flagType: "window",
    key: "--window-y",
    label: "Window Y",
    type: Number,
  },
  DISABLE_WINDOW_RESIZE: {
    default: "false",
    explanation: `This flag disables resizing of the player window in version
      2.56.0 and later. This may make local debugging difficult.`,
    flagType: "window",
    key: "--disable-window-resize",
    label: "Disable Window Resize",
    type: Boolean,
  },
  DISABLE_ALERTS: {
    default: "false",
    explanation: `When this flag is set, the player will not send any alerts.
      (e.g. Device restarted)`,
    flagType: "other",
    key: "--disable-alerts",
    label: "Disable Alerts",
    type: Boolean,
  },
  DISABLE_TRANSITIONS: {
    default: "false",
    explanation: `When this flag is set, the player will disable
      all transitions between content changes.`,
    flagType: "window",
    key: "--disable-transitions",
    label: "Disable Transitions",
    platforms: platforms.ALL,
    type: Boolean,
  },
  DISABLE_WATCHDOG: {
    default: "false",
    explanation: `When this flag is set, the player will not
      start the watchdog process`,
    flagType: "watchdog",
    key: "--disable-watchdog",
    label: "Disable Watchdog",
    type: Boolean,
  },
  DISABLE_WINDOW_FOCUS: {
    default: "false",
    explanation: `When this flag is set, the watchdog will not try to
      focus the player window.`,
    flagType: "watchdog",
    key: "--disable-window-focus",
    label: "Disable Window Focus",
    type: Boolean,
  },
  DISABLE_AUTO_HIDE_TASKBAR: {
    default: "false",
    explanation: `When this flag is set, the watchdog will not try to
      hide the taskbar.`,
    flagType: "watchdog",
    key: "--disable-auto-hide-taskbar",
    label: "Disable Auto-hide Taskbar",
    platforms: [platforms.WINDOWS],
    type: Boolean,
  },
  DISABLE_WINDOW_ALWAYS_ON_TOP: {
    explanation: `When this flag is set, the watchdog will not set
      always-on-top flag for the player window.`,
    flagType: "watchdog",
    key: "--disable-window-always-on-top",
    label: "Disable Window Always on Top",
    type: Boolean,
  },
  ROTATE_SCREEN: {
    default: "0",
    explanation: "Rotates the device screen clockwise by X degrees.",
    flagType: "window",
    key: "--window-rotation",
    label: "Rotate Screen",
    options: [
      {
        label: "0 degrees",
        value: "0",
      },
      {
        label: "90 degrees",
        value: "90",
      },
      {
        label: "180 degrees",
        value: "180",
      },
      {
        label: "270 degrees",
        value: "270",
      },
    ],
    platforms: platforms.ALL,
    type: Array,
  },
  OVERRIDE_NETWORK_FLAGS: {
    default: "false",
    explanation: `When checked, will use device flag settings instead of
      network flags.`,
    flagType: "device",
    key: "--override-network-flags",
    label: "Override network flags with device flags.",
    platforms: platforms.ALL,
    type: Boolean,
  },
  HIDE_WINDOW_ON_STARTUP: {
    default: "false",
    explanation: "When checked, hides player window on device startup",
    flagType: "window",
    key: "--window-hide",
    label: "Hide Window on Startup",
    platforms: platforms.DESKTOP,
    type: Boolean,
  },
  USE_IN_MEMORY_STORAGE_PARTITION: {
    default: "false",
    explanation: `When checked, the player will use an inmemory storage
      partition. Browsing data like cookies and cache will be cleared on
      reboots`,
    flagType: "other",
    key: "--use-in-memory-storage-partition",
    label: "Use In Memory Storage Partition",
    platforms: [platforms.WEBOS],
    type: Boolean,
  },
  ENABLE_MULTIZONE: {
    default: "false",
    explanation:
      "When checked, the player will run with multizone support." +
      "Only works when disable applications flag is enabled.",
    flagType: "other",
    key: "--enable-multizone",
    label: "Enable Multizone",
    platforms: [platforms.WEBOS],
    type: Boolean,
  },
  ENABLE_AUDIO: {
    default: "false",
    explanation: "When checked, the player will play video with audio enabled.",
    flagType: "other",
    key: "--enable-audio",
    label: "Enable Audio",
    platforms: platforms.ALL,
    type: Boolean,
  },
  BIND_SERVER: {
    default: "false",
    explanation:
      "When checked, will run the local server on 0.0.0.0 instead" +
      " of localhost",
    flagType: "other",
    key: "--bind-server-to-all-interfaces",
    label: "Bind Local Server to All Interfaces",
    platforms: [platforms.LINUX, platforms.WINDOWS],
    type: Boolean,
  },
  ENABLE_GPU: {
    default: "false",
    explanation:
      "When checked, the player will enable GPU acceleration. Enabling GPU on Linux requires external dependencies installed on the device; read more",
    explanationLink: {
      href: "https://help.vistarmedia.com/hc/en-us/articles/45536661965197-Enable-or-Disable-GPU-Usage",
      label: "read more",
    },
    flagType: "other",
    key: "--enable-gpu",
    label: "Enable GPU",
    platforms: [platforms.LINUX, platforms.WINDOWS],
    type: Boolean,
    validity: {
      endsIn: null,
      startsIn: "5.0.0",
    },
  },
  ENABLE_WEBVIEW_PERFORMANCE_MONITORING: {
    default: "false",
    explanation:
      "When checked, the player will log details on webview performance.",
    flagType: "other",
    key: "--enable-webview-performance-monitor",
    label: "Enable Webview Performance Monitor",
    platforms: [platforms.LINUX, platforms.WINDOWS],
    type: Boolean,
    validity: {
      endsIn: null,
      startsIn: "4.0.7",
    },
  },
  LOG_RETENTION_DAYS: {
    default: "7",
    endAdornment: "Days",
    explanation: `Sets the TTL/retention time of player logs.`,
    flagType: "other",
    key: "--log-retention-days",
    label: "Player Log Lifespan",
    platforms: [platforms.LINUX, platforms.WEBOS, platforms.WINDOWS],
    type: Number,
    validity: {
      endsIn: null,
      startsIn: "2.49.2",
    },
  },
  TIMESERVER_URL: {
    default: "time.windows.com",
    explanation: `The time server URL your device clock is compared to
      determine its accuracy or drift. If you use VSA, set this to the
      time server you configured your device clock to sync with to make
      logs accurate. `,
    flagType: "other",
    key: "--timeserver-url",
    label: "Timeserver URL",
    platforms: platforms.DESKTOP,
    type: String,
    validity: {
      endsIn: null,
      startsIn: "3.0.2",
    },
  },
  SNAPSHOT_ENABLED: {
    default: "false",
    explanation: `When enabled, the Player triggers an IP webcam to capture
      snapshots during ad playback (when using supported apps like VSA).`,
    flagType: "webcam",
    key: "--snapshot-enabled",
    label: "Enable webcam snapshots ",
    platforms: platforms.DEFAULT,
    type: Boolean,
    validity: {
      endsIn: null,
      startsIn: "4.0.0",
    },
  },
  SNAPSHOT_FREQUENCY: {
    default: 86400,
    endAdornment: "Seconds",
    explanation: `The minimum time between webcam snapshots of a single
      creative. For example, when set to 86400 (24 hours), the Player
      takes a snapshot of each creative once every 24 hours. To take
      snapshots of all ad plays, set this to 0.`,
    flagType: "webcam",
    key: "--snapshot-frequency",
    label: "Snapshot frequency",
    platforms: platforms.DEFAULT,
    type: Number,
    validity: {
      endsIn: null,
      startsIn: "4.0.0",
    },
  },
  SNAPSHOT_WEBCAM_URL: {
    default: null,
    explanation: `When webcam snapshots are enabled, the Player sends a GET
      request to this URL to capture a snapshot. You can include
      camera-specific parameters (e.g., compression, aspect ratio) in the
      URL. To auto-detect an ONVIF-compatible camera on the network instead,
      leave this blank and enable Auto-detect webcam.`,
    flagType: "webcam",
    key: "--snapshot-webcam-url",
    label: "Webcam snapshot URL",
    platforms: platforms.DEFAULT,
    type: String,
    validity: {
      endsIn: null,
      startsIn: "4.0.0",
    },
  },
  SNAPSHOT_LIFESPAN: {
    default: 48,
    endAdornment: "Hours",
    explanation: `The amount of time the device stores webcam snapshots
      locally if it is unable to offload them to Vistar’s server. It
      automatically removes snapshots when it successfully offloads them.`,
    flagType: "webcam",
    key: "--snapshot-lifespan",
    label: "Local snapshot lifespan",
    platforms: platforms.DEFAULT,
    type: Number,
    validity: {
      endsIn: null,
      startsIn: "4.0.0",
    },
  },
  SNAPSHOT_DELETION_WARNING: {
    default: 24,
    endAdornment: "Hours",
    explanation: `How far in advance you want to be notified before webcam
      snapshots that haven’t been offloaded to Vistar’s server are deleted
      from the device. You’ll be notified by  events hourly until the
      snapshots are removed from the device.`,
    flagType: "webcam",
    key: "--snapshot-deletion-warning",
    label: "Snapshot deletion warning (hours)",
    platforms: platforms.DEFAULT,
    type: Number,
    validity: {
      endsIn: null,
      startsIn: "4.0.0",
    },
  },
  SNAPSHOT_AUTO_DETECT: {
    default: "false",
    explanation: `When enabled, the Player scans its network for an
      ONVIF-compatible camera and uses it to take snapshots. Has no effect
      if a Webcam Snapshot URL is set.`,
    flagType: "webcam",
    key: "--snapshot-auto-detect",
    label: "Auto-detect webcam",
    platforms: platforms.DEFAULT,
    type: Boolean,
    validity: {
      endsIn: null,
      startsIn: "4.0.0",
    },
  },
} satisfies Record<string, FlagDefinition>;

const flagsWithDefaults = Object.fromEntries(
  (Object.entries(flags) as [FlagName, FlagDefinition][]).map(
    ([name, flag]) => [name, withDefaultPlatforms(flag)],
  ),
) as Record<FlagName, FlagWithDefaults>;

const flagOptions = Object.values(flagsWithDefaults)
  .filter((flag) => flag.key !== flags.OVERRIDE_NETWORK_FLAGS.key)
  .map(({ label, key }) => ({
    label,
    value: key,
  }));

export default flagsWithDefaults;
export { flagsWithDefaults as Flags, flagOptions };
